import React from "react";
import { canOpenImportConfirmation } from "../validation/import/importValidations.js";

export function buildImportConfirmationDialogModel({
  pendingConfirmationAction,
  selectedFileCount,
  selectedFileEntries,
}) {
  if (pendingConfirmationAction !== "selection" && pendingConfirmationAction !== "force") {
    return null;
  }

  const isForceReplace = pendingConfirmationAction === "force";

  return {
    cancelLabel: "Annuler",
    confirmLabel: isForceReplace ? "Remplacer maintenant" : "Lancer l’import",
    description: isForceReplace
      ? "Les fichiers sélectionnés seront contrôlés par le serveur, puis remplaceront les sources correspondantes et effaceront le planning courant lié."
      : "Les fichiers sélectionnés seront envoyés au serveur pour contrôle, puis ajoutés ou mis à jour sans supprimer les données absentes.",
    eyebrow: isForceReplace ? "Remplacement complet" : "Import Excel",
    impactItems: selectedFileEntries.map((entry) => `${entry.label}: ${entry.filename}`),
    summaryItems: [
      {
        label: "Fichiers sélectionnés",
        value: String(selectedFileCount),
      },
      {
        label: "Mode",
        value: isForceReplace ? "Forcé" : "Analysé",
      },
      ...(isForceReplace ? [
        {
          label: "Action obligatoire",
          value: "Optimiser la flotte",
          tone: "warning",
        },
      ] : []),
    ],
    title: isForceReplace
      ? "Confirmer le remplacement complet des données ?"
      : "Confirmer l’import des fichiers sélectionnés ?",
    tone: isForceReplace ? "warning" : "accent",
    warningMessage: isForceReplace
      ? "Ce remplacement vide le planning courant et l’historique lié aux données remplacées. Cliquez ensuite sur Optimiser la flotte pour recalculer les résultats."
      : "Le serveur pourra encore demander une validation supplémentaire si le planning actif est touché.",
  };
}

export function useImportConfirmationDialog({
  isSubmitting,
  selectedFileCount,
  selectedFileEntries,
  submitImport,
}) {
  const [pendingConfirmationAction, setPendingConfirmationAction] = React.useState(null);

  const closeConfirmationModal = React.useCallback(() => {
    setPendingConfirmationAction(null);
  }, []);

  const openConfirmationModal = React.useCallback((action) => {
    if (!canOpenImportConfirmation({ isSubmitting, selectedFileCount })) {
      return;
    }
    setPendingConfirmationAction(action);
  }, [isSubmitting, selectedFileCount]);

  const handleSubmit = React.useCallback((event) => {
    event.preventDefault();
    openConfirmationModal("selection");
  }, [openConfirmationModal]);

  const handleForceReplaceImport = React.useCallback(() => {
    openConfirmationModal("force");
  }, [openConfirmationModal]);

  const handleConfirmConfirmationDialog = React.useCallback(() => {
    if (pendingConfirmationAction !== "selection" && pendingConfirmationAction !== "force") {
      return;
    }

    closeConfirmationModal();
    // Premier envoi sans confirmation, même forcé : le serveur montre d'abord ce qui serait retiré.
    void submitImport({ submissionMode: pendingConfirmationAction });
  }, [closeConfirmationModal, pendingConfirmationAction, submitImport]);

  const confirmationDialogModel = React.useMemo(() => buildImportConfirmationDialogModel({
    pendingConfirmationAction,
    selectedFileCount,
    selectedFileEntries,
  }), [pendingConfirmationAction, selectedFileCount, selectedFileEntries]);

  const confirmationDialog = React.useMemo(() => {
    if (!confirmationDialogModel) {
      return null;
    }

    return {
      ...confirmationDialogModel,
      isOpen: true,
      onCancel: closeConfirmationModal,
      onClose: closeConfirmationModal,
      onConfirm: handleConfirmConfirmationDialog,
    };
  }, [closeConfirmationModal, confirmationDialogModel, handleConfirmConfirmationDialog]);

  return {
    confirmationDialog,
    handleForceReplaceImport,
    handleSubmit,
  };
}